import CachedIcon from '@mui/icons-material/Cached';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import {
  ByteSizeUnit,
  HashUnit,
  ThemeMode,
  convertBytes,
  convertHash,
  formatBalanceToAbel,
  formatThoundsandNumber,
  stableHash,
  stableHashUnit,
} from '../../../abelcommon';
import { NetworkSummaryInfo } from '../../../api/abel';
import { useTranslation } from 'react-i18next';
import { PoolSummaryInfo } from '../../../api/load-balancer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export const LatestBlockMinedSection = ({
  globalNetworkInfo,
  globalNetworkInfoLoading,
  globalNetworkInfoError,
  refetchGlobalNetworkInfo,
  revenuePerDay,
  summaryDataInfo,
}: {
  globalNetworkInfo: NetworkSummaryInfo;
  globalNetworkInfoLoading: boolean;
  globalNetworkInfoError: AxiosError;
  refetchGlobalNetworkInfo: () => void;
  revenuePerDay: string[];
  summaryDataInfo: PoolSummaryInfo;
}) => {
  const { t } = useTranslation();
  const [milisecTimeCounter, setMilisecTimeCounter] = useState(0);
  useEffect(() => {
    if (globalNetworkInfoLoading || !summaryDataInfo) return;

    setMilisecTimeCounter(summaryDataInfo.payoutOverview.nextDistribution.nextDistributionTimeInSecond * 1000);
  }, [summaryDataInfo, globalNetworkInfoLoading]);

  const nextDistributionTime = useMemo(() => {
    if (milisecTimeCounter <= 0) {
      return {
        hours: '00',
        minutes: '00',
        seconds: '00',
      };
    }

    const diffInHours = Math.floor(milisecTimeCounter / (1000 * 60 * 60));
    const diffInMinutes = Math.floor((milisecTimeCounter / (1000 * 60)) % 60);
    const diffInSecond = Math.floor((milisecTimeCounter / 1000) % 60);

    return {
      hours: diffInHours === 0 ? '00' : `${diffInHours < 10 ? '0' : ''}${diffInHours}`,
      minutes: diffInMinutes === 0 ? '00' : `${diffInMinutes < 10 ? '0' : ''}${diffInMinutes}`,
      seconds: diffInSecond === 0 ? '00' : `${diffInSecond < 10 ? '0' : ''}${diffInSecond}`,
    };
  }, [milisecTimeCounter]);

  const latestBlockMinedInfo = useMemo(() => {
    const propsPlaceholder: { key: string; data: LatestBlockMinedInfoBoxItem[] }[] = [
      t('latestBlockMinedSectionPage.latestBlock'),
      t('latestBlockMinedSectionPage.averageBlockTime'),
      t('latestBlockMinedSectionPage.networkDifficulty'),
      t('latestBlockMinedSectionPage.averageNetworkHashRate'),
      t('latestBlockMinedSectionPage.totalLedgerSize'),
      t('latestBlockMinedSectionPage.totalNumberOfAddresses'),
      t('latestBlockMinedSectionPage.minerOnline'),
      t('latestBlockMinedSectionPage.1GHsRevenue'),
      t('latestBlockMinedSectionPage.poolHashrate'),
      t('latestBlockMinedSectionPage.poolFee'),
      t('latestBlockMinedSectionPage.algorithm'),
      t('latestBlockMinedSectionPage.blockReward'),
      t('latestBlockMinedSectionPage.nextPayout'),
      t('latestBlockMinedSectionPage.payoutThreshold'),
      t('latestBlockMinedSectionPage.totalPayout'),
    ].map((key) => ({ key, data: [] }));

    return {
      gridItemStyles: {
        xs: 12,
        sm: 6,
        lg: 4,
        xl: 4,
      },
      props:
        !globalNetworkInfo || !summaryDataInfo
          ? propsPlaceholder
          : [
              {
                key: t('latestBlockMinedSectionPage.latestBlock'),
                data: [
                  {
                    text: formatThoundsandNumber(globalNetworkInfo.latestBlock.height),
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.latestBlock'),
                    isTitle: false,
                    isContent: true,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.averageBlockTime'),
                data: [
                  {
                    text: `${formatThoundsandNumber(globalNetworkInfo.avgBlockTimeLast1k)}s ${t('latestBlockMinedSectionPage.overTheLatest1,000Blocks')}`,
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: `${formatThoundsandNumber(globalNetworkInfo.avgBlockTimeLast100)}s ${t('latestBlockMinedSectionPage.overTheLatest100Blocks')}`,
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.averageBlockTime'),
                    isTitle: false,
                    isContent: true,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.networkDifficulty'),
                data: [
                  {
                    text: `${formatThoundsandNumber(globalNetworkInfo.latestBlock.difficulty)}`,
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.networkDifficulty'),
                    isTitle: false,
                    isContent: true,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.averageNetworkHashRate'),
                data: [
                  {
                    text: `${formatThoundsandNumber(
                      convertHash(globalNetworkInfo.networkHashRateLast1K, HashUnit.GH)
                    )} GH/s`,
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.averageNetworkHashRate'),
                    isTitle: false,
                    isContent: true,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.overTheLatest1,000Blocks'),
                    isTitle: false,
                    isContent: false,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.totalLedgerSize'),
                data: [
                  {
                    text: `${formatThoundsandNumber(convertBytes(globalNetworkInfo.totalFullSize, ByteSizeUnit.MB), 0)} ${
                      ByteSizeUnit.MB
                    }`,
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.totalLedgerSize'),
                    isTitle: false,
                    isContent: true,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.totalNumberOfAddresses'),
                data: [
                  {
                    text: formatThoundsandNumber(globalNetworkInfo.totalAddress),
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.totalNumberOfAddresses'),
                    isTitle: false,
                    isContent: true,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.minerOnline'),
                data: [
                  {
                    text: formatThoundsandNumber(summaryDataInfo.totalMachineCount),
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.minerOnline'),
                    isTitle: false,
                    isContent: true,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.1GHsRevenue'),
                data: [
                  {
                    text: `${parseFloat(revenuePerDay[0]).toFixed(2)} ABEL`,
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.1GHsRevenue'),
                    isTitle: false,
                    isContent: true,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.poolHashrate'),
                data: [
                  {
                    text: `${formatThoundsandNumber(stableHash(summaryDataInfo.hashrate * 1000), 4)} ${stableHashUnit(
                      summaryDataInfo.hashrate * 1000
                    )}/s`,
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.poolHashrate'),
                    isTitle: false,
                    isContent: true,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.poolFee'),
                data: [
                  {
                    text: `${formatThoundsandNumber(summaryDataInfo.poolFee)}%`,
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.poolFee'),
                    isTitle: false,
                    isContent: true,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.algorithm'),
                data: [
                  {
                    text: 'ETHash-ABEL',
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.algorithm'),
                    isTitle: false,
                    isContent: true,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.blockReward'),
                data: [
                  {
                    text: `${Math.floor(globalNetworkInfo.latestBlock.totalfee)} ABEL + Tx Fees`,
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.blockReward'),
                    isTitle: false,
                    isContent: true,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.nextPayout'),
                data: [
                  {
                    text: `@Block ${!summaryDataInfo.payoutOverview ? 0 : summaryDataInfo.payoutOverview.nextDistribution.currentBlockHeight}`,
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.nextPayout'),
                    isTitle: false,
                    isContent: true,
                  },
                  {
                    text: `In ${nextDistributionTime.hours}h ${nextDistributionTime.minutes}m ${nextDistributionTime.seconds}s`,
                    isTitle: false,
                    isContent: false,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.payoutThreshold'),
                data: [
                  {
                    text: `${formatThoundsandNumber(summaryDataInfo.payoutThreshold)} ABEL`,
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.payoutThreshold'),
                    isTitle: false,
                    isContent: true,
                  },
                  {
                    text: `(${t('latestBlockMinedSectionPage.adjustable')})`,
                    isTitle: false,
                    isContent: false,
                  },
                ],
              },
              {
                key: t('latestBlockMinedSectionPage.totalPayout'),
                data: [
                  {
                    text: `${formatBalanceToAbel(summaryDataInfo.totalPayout).split('.')[0]} ABEL`,
                    isTitle: true,
                    isContent: false,
                  },
                  {
                    text: t('latestBlockMinedSectionPage.totalPayout'),
                    isTitle: false,
                    isContent: true,
                  },
                ],
              },
            ],
    };
  }, [globalNetworkInfo, nextDistributionTime, revenuePerDay, summaryDataInfo, t]);

  return (
    <Card>
      <CardHeader
        title={t('latestBlockMinedSectionPage.networkOverview')}
        action={
          <IconButton aria-label="reload" onClick={() => refetchGlobalNetworkInfo()}>
            <CachedIcon />
          </IconButton>
        }
      />
      <CardContent sx={{ overflow: 'auto' }}>
        {globalNetworkInfoError ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {globalNetworkInfoError?.message}
          </Alert>
        ) : (
          <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
            {latestBlockMinedInfo.props.map(({ key, data }) => {
              const generatedKey = key.split(' ').join('-');

              return (
                <Grid item {...latestBlockMinedInfo.gridItemStyles} key={generatedKey}>
                  <LatestBlockMinedInfoBox data={data} isLoading={globalNetworkInfoLoading} />
                </Grid>
              );
            })}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

interface LatestBlockMinedInfoBoxItem {
  text: string;
  isTitle: boolean;
  isContent: boolean;
}

interface LatestBlockMinedInfoBoxProps {
  data: LatestBlockMinedInfoBoxItem[];
  isLoading: boolean;
}

const LatestBlockMinedInfoBox = ({ data, isLoading }: LatestBlockMinedInfoBoxProps) => {
  const theme = useTheme();
  const dataToShow = data.map(({ text, isTitle, isContent }, index) => {
    return (
      <Typography
        key={index}
        variant={isTitle ? 'h6' : isContent ? 'body2' : 'subtitle2'}
        sx={{
          fontWeight: isTitle ? 600 : isContent ? 500 : 400,
          color: isTitle ? 'text.primary' : isContent ? 'text.secondary' : 'text.secondary',
        }}
      >
        {text}
      </Typography>
    );
  });

  return isLoading ? (
    <Skeleton variant="rectangular" sx={{ minHeight: '82px', borderRadius: 1 }} />
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        boxShadow: 1,
        padding: '8px',
        border: 1,
        borderColor: theme.palette.appGray.dark,
        borderRadius: 1,
        minHeight: '82px',
        ...(theme.palette.mode !== ThemeMode.Light
          ? {}
          : {
              backgroundColor: theme.palette.primary.light,
              borderColor: 'transparent',
              boxShadow: 'none',
            }),
      }}
    >
      {dataToShow}
    </Box>
  );
};
